import Vue from 'vue';
import { addNotification, clearNotification } from '@/utils/notification';
import { getSignature } from '@/helpers/api/launchpad';
import { token, products } from '@/constants/launchpad';

export const state = {
  loading: false,
  saleId: null,
  paymentTokenName: '',
  paymentTokenAddress: '',
  userBalance: null,
  allowance: 0,
  price: null,
  closedAfter: null,
  activeAfter: null,
  notStartedYet: true,
  isFinished: false,
  stage: 0,
  stake: {},
  userStake: 0,
  participants: 0,
  currentParticipants: 0,
  solana: '',
  bought: false,
  claimed: false,
  allocation: 0,
  allocationTokenName: '',
  pricePerToken: null,
  saleContract: 'launchpadSimpleSale',
};

export const getters = {
  loading: (s) => s.loading,
  saleId: (s) => s.saleId,
  paymentTokenName: (s) => s.paymentTokenName,
  paymentTokenAddress: (s) => s.paymentTokenAddress,
  userBalance: (s) => s.userBalance,
  allowance: (s) => s.allowance,
  price: (s) => s.price,
  closedAfter: (s) => s.closedAfter,
  activeAfter: (s) => s.activeAfter,
  notStartedYet: (s) => s.notStartedYet,
  isFinished: (s) => s.isFinished,
  stage: (s) => s.stage,
  stake: (s) => s.stake,
  userStake: (s) => s.userStake,
  participants: (s) => s.participants,
  solana: (s) => s.solana,
  bought: (s) => s.bought,
  claimed: (s) => s.claimed,
  currentParticipants: (s) => s.currentParticipants,
  allocation: (s) => s.allocation,
  allocationTokenName: (s) => s.allocationTokenName,
  pricePerToken: (s) => s.pricePerToken,
  saleContract: (s) => s.saleContract,
};

export const mutations = {
  setLoading: (state, payload) => {
    state.loading = payload;
  },
  setSaleId: (state, payload) => {
    state.saleId = payload;
  },
  setPaymentTokenName: (state, payload) => {
    state.paymentTokenName = payload;
  },
  setPaymentTokenAddress: (state, payload) => {
    state.paymentTokenAddress = payload;
  },
  setBalance: (state, payload) => {
    state.userBalance = payload;
  },
  setPrice: (state, payload) => {
    state.price = payload;
  },
  setAllowance: (state, payload) => {
    state.allowance = payload;
  },
  setClosedAfter: (state, payload) => {
    state.closedAfter = payload;
  },
  setActiveAfter: (state, payload) => {
    state.activeAfter = payload;
  },
  setNotStartedYet: (state, payload) => {
    state.notStartedYet = payload;
  },
  setIsFinished: (state, payload) => {
    state.isFinished = payload;
  },
  setStage: (state, payload) => {
    state.stage = payload;
  },
  setStake: (state, payload) => {
    state.stake = payload;
  },
  setUserStake: (state, payload) => {
    state.userStake = payload;
  },
  setParticipants: (state, payload) => {
    state.participants = payload;
  },
  setSolana: (state, payload) => {
    state.solana = payload;
  },
  setBought: (state, payload) => {
    state.bought = payload;
  },
  setClaimed: (state, payload) => {
    state.claimed = payload;
  },
  setCurrentParticipants: (state, payload) => {
    state.currentParticipants = payload;
  },
  setAllocation: (state, payload) => {
    state.allocation = payload;
  },
  setAllocationTokenName: (state, payload) => {
    state.allocationTokenName = payload;
  },
  setPricePerToken: (state, payload) => {
    state.pricePerToken = payload;
  },
  setSaleContract: (state, payload) => {
    state.saleContract = payload;
  },
};

export const actions = {
  async allowanceToken({ rootState, commit, state }) {
    try {
      const tokenAllowance = Vue.$contracts.getContract(state.paymentTokenName).methods.allowance;
      const sale = Vue.$contracts.getAddress('launchpadSimpleSale');
      const allowance = await tokenAllowance(rootState.User.ethAddress, sale).call();
      commit('setAllowance', allowance);
    } catch (e) {
      console.log(e);
    }
  },

  async getStage({ commit, state }) {
    try {
      const sale = Vue.$contracts.getContract(state.saleContract).methods;
      const stage = await sale.getCurrentStage(state.saleId).call();
      commit('setStage', Number(stage));
      return Number(stage);
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  async getUserStake({ rootState, commit }, { stakeToken }) {
    try {
      const contract = stakeToken === 'SHAKE' ? 'launchpadStakingShake' : 'launchpadStaking';
      const staking = Vue.$contracts.getContract(contract).methods;
      const stake = await staking.userStakes(rootState.User.ethAddress).call();
      commit('setUserStake', stake?.amount);
    } catch (e) {
      console.log(e);
    }
  },

  async approveToken({ rootState, state, commit }) {
    try {
      commit('setLoading', true);
      const launchpadSimpleSaleAddress = Vue.$contracts.getAddress(state.saleContract);
      const tokenApprove = Vue.$contracts.getContract(state.paymentTokenName).methods.approve;
      await tokenApprove(launchpadSimpleSaleAddress, Vue.$BN(2).pow(256).minus(1).toString())
        .send({
          from: rootState.User.ethAddress,
        })
        .once('transactionHash', (hash) => {
          addNotification(
            {
              type: 'info',
              title: 'Pending approve',
              data: { hash },
            },
            { root: true },
          );
        })
        .once('confirmation', () => {
          clearNotification('info');
          addNotification(
            {
              type: 'success',
              title: 'Successfully approved!',
              config: { duration: 15000 },
            },
            { root: true },
          );
        })
        .once('error', () => {
          clearNotification('info');
          addNotification(
            {
              type: 'warn',
              title: 'Approve failed',
              config: { duration: 5000 },
            },
            { root: true },
          );
        });

      commit('setLoading', false);
      this.dispatch('Simplesale/allowanceToken');
    } catch (e) {
      console.log(e);
      commit('setLoading', false);
    }
  },

  async getParticipants({ commit, state }) {
    try {
      commit('setLoading', true);
      const sale = Vue.$contracts.getContract(state.saleContract);
      if (!sale) return;
      const res = await sale.methods.sales(state.saleId).call();
      const { participants, soldTokens, amountPerUser } = res;
      const currentParticipants = Math.floor(soldTokens / amountPerUser);
      commit('setParticipants', Number(participants));
      commit('setCurrentParticipants', Number(currentParticipants));
      commit('setLoading', false);
    } catch (e) {
      console.log(e);
      commit('setLoading', false);
    }
  },

  async buy({ commit, rootState, state }) {
    try {
      commit('setLoading', true);
      await this.dispatch('Simplesale/getParticipants');
      if (state.currentParticipants >= state.participants) {
        commit('setLoading', false);
        addNotification(
          {
            type: 'warn',
            title: 'Participants limit exceeded',
            config: { duration: 15000 },
          },
          { root: true },
        );
        return;
      }
      const { signature } = await getSignature(rootState.User.ethAddress);
      await Vue.$contracts
        .getContract('launchpadSimpleSale')
        .methods.buy(state.saleId, signature)
        .send({ from: rootState.User.ethAddress })
        .once('transactionHash', (hash) => {
          addNotification(
            {
              type: 'info',
              title: 'Pending register sale:',
              data: { hash },
            },
            { root: true },
          );
        })
        .once('confirmation', () => {
          clearNotification('info');
          addNotification(
            {
              type: 'success',
              title: 'Successfully registered!',
              config: { duration: 15000 },
            },
            { root: true },
          );
          this.$emit('update');
          commit('setLoading', false);
        })
        .once('error', () => {
          clearNotification('info');
          addNotification(
            {
              type: 'warn',
              title: 'Register sale failed',
              config: { duration: 5000 },
            },
            { root: true },
          );
          commit('setLoading', false);
        });
      this.dispatch('Simplesale/updateUsers');
    } catch (e) {
      console.log(e);
      commit('setLoading', false);
    }
  },

  async claim({ commit, rootState, state }) {
    try {
      commit('setLoading', true);
      await Vue.$contracts
        .getContract('launchpadSimpleSale')
        .methods.claim(state.saleId)
        .send({ from: rootState.User.ethAddress })
        .once('transactionHash', (hash) => {
          addNotification(
            {
              type: 'info',
              title: 'Pending claim:',
              data: { hash },
            },
            { root: true },
          );
        })
        .once('confirmation', () => {
          clearNotification('info');
          addNotification(
            {
              type: 'success',
              title: 'Successfully claimed!',
              config: { duration: 15000 },
            },
            { root: true },
          );
          this.$emit('update');
          this.isLoading = false;
        })
        .once('error', () => {
          clearNotification('info');
          addNotification(
            {
              type: 'warn',
              title: 'Claim failed',
              config: { duration: 5000 },
            },
            { root: true },
          );
          this.isLoading = false;
        });
      commit('setLoading', false);
      this.dispatch('Simplesale/updateUsers');
    } catch (e) {
      console.log(e);
      commit('setLoading', false);
    }
  },

  async setSolana({ commit }, { solana }) {
    commit('setSolana', solana);
  },

  async updateUsers({ rootState, commit, state }) {
    const sale = Vue.$contracts.getContract(state.saleContract).methods;
    const users = await sale.users(rootState.User.ethAddress, state.saleId).call();
    const { bought, claimed } = users;
    commit('setBought', bought);
    commit('setClaimed', claimed);
  },

  async saleDate({ commit }, saleId) {
    try {
      const sale = Vue.$contracts.getContract(state.saleContract);
      if (!sale) return;
      const saleRecord = await sale.methods.getSaleRecord(saleId).call();
      const timeStamps = saleRecord[6];
      const now = Math.floor(Date.now() / 1000);
      const notStarted = Number(timeStamps[0]) > now;
      const isFinished = Number(timeStamps[3]) < now;

      // console.log('not started', Number(timeStamps[0]) > now);
      // console.log('finished', Number(timeStamps[3]) < now);

      // if (state.stage === 0 && notStarted) {
      //   commit('setClosedAfter', timeStamps[3]);
      //   commit('setActiveAfter', timeStamps[0]);
      // } else if (state.stage === 1) {
      //   commit('setClosedAfter', timeStamps[1]);
      //   commit('setActiveAfter', timeStamps[0]);
      // } else {
      //   commit('setClosedAfter', timeStamps[3]);
      //   commit('setActiveAfter', timeStamps[2]);
      // }

      commit('setClosedAfter', timeStamps[1]);
      commit('setActiveAfter', timeStamps[0]);
      commit('setNotStartedYet', notStarted);
      commit('setIsFinished', isFinished);
    } catch (e) {
      console.log(e);
    }
  },

  async getUserBalance({ rootState, commit }) {
    try {
      const tokenContract = Vue.$contracts.getContract(state.paymentTokenName).methods;
      const balance = await tokenContract.balanceOf(rootState.User.ethAddress).call();
      commit('setBalance', balance);
    } catch (e) {
      console.log(e);
    }
  },

  async getSaleInfo({ commit }, { saleId, stakeToken, pid }) {
    try {
      commit('setSaleId', saleId);
      const contract = products[pid]?.saleContract ? products[pid].saleContract : 'launchpadSimpleSale';
      commit('setSaleContract', contract);
      const sale = Vue.$contracts.getContract(state.saleContract);
      if (!sale) return;
      const res = await sale.methods.sales(saleId).call();
      const {
        minStake, maxStake, participants, soldTokens, amountPerUser, price, saleToken,
      } = res;
      const paymentToken = await sale.methods.paymentToken().call();
      const tokenName = token[paymentToken].name;
      const allocationTokenName = token[saleToken]?.name || '';
      const currentParticipants = Math.floor(soldTokens / amountPerUser);
      commit('setPaymentTokenAddress', paymentToken);
      commit('setPaymentTokenName', tokenName);
      commit('setPricePerToken', price);
      commit('setPrice', Vue.$BN(price).times(amountPerUser).div(Vue.$BN(10).pow(18)));
      commit('setStake', { minStake, maxStake });
      commit('setParticipants', Number(participants));
      commit('setCurrentParticipants', Number(currentParticipants));
      commit('setAllocation', amountPerUser);
      commit('setAllocationTokenName', allocationTokenName);
      this.dispatch('Simplesale/getUserBalance');
      this.dispatch('Simplesale/allowanceToken');
      this.dispatch('Simplesale/getUserStake', { stakeToken });
      this.dispatch('Simplesale/getStage');
      this.dispatch('Simplesale/updateUsers');
      // const { solAddress } = await getSignature(rootState.User.ethAddress);
      // if (solAddress) {
      //   commit('setSolana', solAddress);
      // }
    } catch (e) {
      console.log(e);
    }
  },
};
